import React from 'react';
import { LeaderboardEntry } from '../data/mockData';
import { MetricBadge } from './MetricBadge';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
interface Props {
  entry: LeaderboardEntry;
  rank: number;
  maxScore: number;
}
export function LeaderboardRow({
  entry,
  rank,
  maxScore
}: Props) {
  const getTrendIcon = () => {
    if (entry.trend === 'up') return <TrendingUp className="w-3.5 h-3.5 text-ez-green" data-id="element-44" />;
    if (entry.trend === 'down') return <TrendingDown className="w-3.5 h-3.5 text-red-500" data-id="element-45" />;
    return <Minus className="w-3.5 h-3.5 text-gray-300" data-id="element-46" />;
  };
  const progressWidth = Math.max(5, entry.score / maxScore * 100);
  return <div className="group bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:border-ez-green/40 transition-all duration-200 relative overflow-hidden" data-id="element-47">
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 h-1 bg-ez-green/10 transition-all duration-700 ease-out" style={{
      width: `${progressWidth}%`
    }} data-id="element-48" />
      <div className="absolute bottom-0 left-0 h-1 bg-ez-green/50 transition-all duration-700 ease-out" style={{
      width: `${progressWidth}%`
    }} data-id="element-49" />

      <div className="flex items-center gap-3 sm:gap-4 relative z-10" data-id="element-50">
        {/* Rank */}
        <div className="flex-shrink-0 w-8 flex justify-center" data-id="element-51">
          <span className="text-gray-400 font-bold text-base tabular-nums" data-id="element-52">
            {rank}
          </span>
        </div>

        {/* Avatar & Info */}
        <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-53">
          {entry.avatar ? <img src={entry.avatar} alt={entry.name} className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-100 flex-shrink-0" data-id="element-54" /> : <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-ez-dark text-white flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-55">
              {entry.name.charAt(0)}
            </div>}
          <div className="min-w-0" data-id="element-56">
            <h3 className="font-semibold text-gray-900 text-sm truncate flex items-center gap-1.5" data-id="element-57">
              {entry.name}
              {getTrendIcon()}
            </h3>
            {entry.teamName && <p className="text-xs text-gray-400 truncate" data-id="element-58">{entry.teamName}</p>}
          </div>
        </div>

        {/* Badges */}
        <div className="hidden md:flex flex-wrap gap-1.5 w-32 xl:w-44 flex-shrink-0" data-id="element-59">
          {entry.badges.map(badge => <MetricBadge key={badge} badge={badge} data-id="element-60" />)}
        </div>

        {/* Metrics */}
        <div className="hidden sm:flex items-center gap-6 lg:gap-8 flex-shrink-0" data-id="element-61">
          <div className="flex flex-col items-end w-10" data-id="element-62">
            <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-63">
              {entry.docOuts}
            </span>
            <span className="text-[9px] uppercase tracking-wider font-semibold text-gray-400" data-id="element-64">
              Docs
            </span>
          </div>
          <div className="flex flex-col items-end w-10" data-id="element-65">
            <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-66">
              {entry.submissions}
            </span>
            <span className="text-[9px] uppercase tracking-wider font-semibold text-gray-400" data-id="element-67">
              Subs
            </span>
          </div>
          <div className="flex flex-col items-end w-10" data-id="element-68">
            <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-69">
              {entry.ctcs}
            </span>
            <span className="text-[9px] uppercase tracking-wider font-semibold text-gray-400" data-id="element-70">
              CTCs
            </span>
          </div>
        </div>

        {/* Score */}
        <div className="flex-shrink-0 w-20 lg:w-24 text-right" data-id="element-71">
          <div className="text-base lg:text-lg font-black text-ez-dark tracking-tight tabular-nums" data-id="element-72">
            {entry.score.toLocaleString()}
          </div>
          {entry.pointDelta != null && entry.pointDelta !== 0 ? <div className={`text-[10px] font-bold tracking-wide flex items-center justify-end gap-0.5 ${entry.pointDelta > 0 ? 'text-ez-green' : 'text-red-500'}`} data-id="element-73">
              {entry.pointDelta > 0 ? <TrendingUp className="w-3 h-3" data-id="element-74" /> : <TrendingDown className="w-3 h-3" data-id="element-75" />}
              {entry.pointDelta > 0 ? '+' : ''}
              {entry.pointDelta} today
            </div> : <div className="text-[9px] text-gray-400 uppercase font-bold tracking-wider" data-id="element-76">
              Points
            </div>}
        </div>
      </div>
    </div>;
}